/**
 * Frontend client for the platform modules.
 *
 * Every method propagates a correlation_id (mints a fresh one if not provided)
 * so a single UI action — e.g. clicking "Run end-to-end" — produces a single
 * trace across marketdata → strategy → algo → grid → dispatch.
 *
 * All five module endpoints follow the same shape: `?action=…` for shared
 * observability actions (health, events, trace, stats, debug, clear), plus
 * module-specific verbs documented inline.
 */

import { API_BASE_URL } from '../config/api';

const PLATFORM_PREFIX = `${API_BASE_URL}`.replace(/\/$/, '');

function uuid() {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID();
  }
  // RFC4122 v4 fallback
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
    const r = (Math.random() * 16) | 0;
    return (c === 'x' ? r : (r & 0x3) | 0x8).toString(16);
  });
}

export const newCorrelationId = (label = 'ui') =>
  `${label}-${Date.now().toString(36)}-${uuid().slice(0, 8)}`;

async function call(module, { method = 'GET', query = {}, body, correlationId, causationId } = {}) {
  const params = new URLSearchParams();
  for (const [k, v] of Object.entries(query)) {
    if (v !== undefined && v !== null && v !== '') params.set(k, String(v));
  }
  const qs = params.toString() ? `?${params.toString()}` : '';
  const url = `${PLATFORM_PREFIX}/api/${module}${qs}`;
  const headers = { Accept: 'application/json' };
  if (correlationId) headers['x-correlation-id'] = correlationId;
  if (causationId) headers['x-causation-id'] = causationId;
  const init = { method, headers };
  if (body !== undefined) {
    headers['Content-Type'] = 'application/json';
    init.body = JSON.stringify(body);
  }
  const res = await fetch(url, init);
  const text = await res.text();
  let parsed;
  try { parsed = text ? JSON.parse(text) : {}; } catch { parsed = { raw: text }; }
  if (!res.ok) {
    const err = new Error(parsed?.error || `HTTP ${res.status}`);
    err.status = res.status;
    err.body = parsed;
    throw err;
  }
  return parsed;
}

// ─── Market Data ────────────────────────────────────────────────────────────
export const marketdata = {
  meta: ({ adapter } = {}) => call('marketdata', { query: { type: 'meta', adapter } }),
  orderbook: ({ market = 'GB', product = 'DA', period, adapter, correlationId } = {}) =>
    call('marketdata', { query: { type: 'orderbook', market, product, period, adapter }, correlationId }),
  depthHorizon: ({ market = 'GB', product = 'ID', horizon_sps = 12, adapter, correlationId } = {}) =>
    call('marketdata', { query: { type: 'depth_horizon', market, product, horizon_sps, adapter }, correlationId }),
  priceHistory: ({ market = 'GB', product = 'ID', period, minutes = 30, adapter, correlationId } = {}) =>
    call('marketdata', { query: { type: 'price_history', market, product, period, minutes, adapter }, correlationId }),
  tape: ({ market = 'GB', product = 'DA', period, limit, adapter, correlationId } = {}) =>
    call('marketdata', { query: { type: 'tape', market, product, period, limit, adapter }, correlationId }),
  reference: ({ market = 'GB', product = 'DA', period, adapter, correlationId } = {}) =>
    call('marketdata', { query: { type: 'reference', market, product, period, adapter }, correlationId }),
  forecast: ({ market = 'GB', kind = 'wind', horizon_hours, adapter, correlationId } = {}) =>
    call('marketdata', { query: { type: 'forecast', market, kind, horizon_hours, adapter }, correlationId }),
  commodity: ({ instrument = 'TTF', adapter, correlationId } = {}) =>
    call('marketdata', { query: { type: 'commodity', instrument, adapter }, correlationId }),
};

// ─── Strategy ───────────────────────────────────────────────────────────────
export const strategy = {
  plants: ({ correlationId } = {}) =>
    call('strategy', { query: { action: 'plants' }, correlationId }),
  upsertPlant: (plant, { correlationId } = {}) =>
    call('strategy', { method: 'POST', query: { action: 'plant' }, body: plant, correlationId }),
  run: ({ market = 'GB', product = 'DA', correlationId } = {}) =>
    call('strategy', { method: 'POST', query: { action: 'run' }, body: { market, product }, correlationId }),
  runs: ({ correlationId, limit } = {}) =>
    call('strategy', { query: { action: 'runs', limit }, correlationId }),
  intents: ({ flow_correlation_id, run_id, limit, correlationId } = {}) =>
    call('strategy', { query: { action: 'intents', flow_correlation_id, run_id, limit }, correlationId }),
};

// ─── Algo ───────────────────────────────────────────────────────────────────
export const algo = {
  submit: (intents, { correlationId } = {}) =>
    call('algo', { method: 'POST', query: { action: 'submit' }, body: { intents }, correlationId }),
  cancel: (order_id, { correlationId } = {}) =>
    call('algo', { method: 'POST', query: { action: 'cancel' }, body: { order_id }, correlationId }),
  // Kill-switch — cancels all open orders. Pass plant_id to scope to one
  // plant; omit to cancel everything across the platform.
  cancelAll: ({ plant_id } = {}, { correlationId } = {}) =>
    call('algo', { method: 'POST', query: { action: 'cancel_all' }, body: { plant_id: plant_id || null }, correlationId }),
  orders: ({ flow_correlation_id, status, plant_id, limit, correlationId } = {}) =>
    call('algo', { query: { action: 'orders', flow_correlation_id, status, plant_id, limit }, correlationId }),
  fills: ({ flow_correlation_id, order_id, limit, correlationId } = {}) =>
    call('algo', { query: { action: 'fills', flow_correlation_id, order_id, limit }, correlationId }),
  positions: ({ correlationId } = {}) =>
    call('algo', { query: { action: 'positions' }, correlationId }),
};

// ─── Grid ───────────────────────────────────────────────────────────────────
export const grid = {
  submitPN: ({ plant_id, period, mw }, { correlationId } = {}) =>
    call('grid', { method: 'POST', query: { action: 'pn' }, body: { plant_id, period, mw }, correlationId }),
  submitBOD: ({ plant_id, period, bids, offers }, { correlationId } = {}) =>
    call('grid', { method: 'POST', query: { action: 'bod' }, body: { plant_id, period, bids, offers }, correlationId }),
  submitPositions: (positions, { correlationId } = {}) =>
    call('grid', { method: 'POST', query: { action: 'submit_positions' }, body: { positions }, correlationId }),
  submissions: ({ flow_correlation_id, plant_id, type, limit, correlationId } = {}) =>
    call('grid', { query: { action: 'submissions', flow_correlation_id, plant_id, type, limit }, correlationId }),
  acceptances: ({ flow_correlation_id, submission_id, limit, correlationId } = {}) =>
    call('grid', { query: { action: 'acceptances', flow_correlation_id, submission_id, limit }, correlationId }),
};

// ─── Dispatch ───────────────────────────────────────────────────────────────
export const dispatch = {
  setpoint: ({ plant_id, period, mw, source }, { correlationId } = {}) =>
    call('dispatch', { method: 'POST', query: { action: 'setpoint' }, body: { plant_id, period, mw, source }, correlationId }),
  applyAcceptances: (acceptances, { correlationId } = {}) =>
    call('dispatch', { method: 'POST', query: { action: 'apply_acceptances' }, body: { acceptances }, correlationId }),
  telemetry: ({ plant_id, correlationId } = {}) =>
    call('dispatch', { query: { action: 'telemetry', plant_id }, correlationId }),
  setpointEvents: ({ flow_correlation_id, plant_id, limit, correlationId } = {}) =>
    call('dispatch', { query: { action: 'setpoints', flow_correlation_id, plant_id, limit }, correlationId }),
};

// ─── Strategy v2 extensions ────────────────────────────────────────────────
strategy.setMode = (plant_id, mode, { correlationId } = {}) =>
  call('strategy', { method: 'POST', query: { action: 'set_mode' }, body: { plant_id, mode }, correlationId });
strategy.setModeBulk = (updates, { correlationId } = {}) =>
  call('strategy', { method: 'POST', query: { action: 'set_mode_bulk' }, body: { updates }, correlationId });
strategy.horizons = ({ plant_id, limit, correlationId } = {}) =>
  call('strategy', { query: { action: 'horizons', plant_id, limit }, correlationId });
strategy.openingPositions = ({ plant_id, correlationId } = {}) =>
  call('strategy', { query: { action: 'opening_positions', plant_id }, correlationId });

// ─── Algo / Execution v2 extensions ────────────────────────────────────────
algo.plans = ({ flow_correlation_id, plant_id, status, limit, correlationId } = {}) =>
  call('algo', { query: { action: 'plans', flow_correlation_id, plant_id, status, limit }, correlationId });
algo.fireSlice = (plan_id, seq, { correlationId } = {}) =>
  call('algo', { method: 'POST', query: { action: 'fire_slice' }, body: { plan_id, seq }, correlationId });
algo.firePlan = (plan_id, { correlationId } = {}) =>
  call('algo', { method: 'POST', query: { action: 'fire_plan' }, body: { plan_id }, correlationId });

// ─── Grid v2 extensions ────────────────────────────────────────────────────
grid.draftPN = ({ plant_id, period, mw, source_position }, { correlationId } = {}) =>
  call('grid', { method: 'POST', query: { action: 'draft_pn' }, body: { plant_id, period, mw, source_position }, correlationId });
grid.firmPN = ({ plant_id, period }, { correlationId } = {}) =>
  call('grid', { method: 'POST', query: { action: 'firm_pn' }, body: { plant_id, period }, correlationId });
grid.draftPNs = ({ plant_id, correlationId } = {}) =>
  call('grid', { query: { action: 'draft_pns', plant_id }, correlationId });

// ─── Orchestrator (the central "platform brain" — Phase 1) ─────────────────
export const orchestrator = {
  status: ({ correlationId } = {}) =>
    call('orchestrator', { query: { action: 'status' }, correlationId }),
  tick: (plant_ids, { correlationId } = {}) =>
    call('orchestrator', { method: 'POST', query: { action: 'tick' }, body: { plant_ids }, correlationId }),
  setMode: (plant_id, mode, { correlationId } = {}) =>
    call('orchestrator', { method: 'POST', query: { action: 'set_mode' }, body: { plant_id, mode }, correlationId }),
  setModeBulk: (updates, { correlationId } = {}) =>
    call('orchestrator', { method: 'POST', query: { action: 'set_mode_bulk' }, body: { updates }, correlationId }),
  approveIntent: (intent_id, { override_mode, correlationId } = {}) =>
    call('orchestrator', { method: 'POST', query: { action: 'approve_intent' }, body: { intent_id, override_mode }, correlationId }),
  approvePN: ({ plant_id, period }, { correlationId } = {}) =>
    call('orchestrator', { method: 'POST', query: { action: 'approve_pn' }, body: { plant_id, period }, correlationId }),
  approveDispatch: ({ plant_id, period }, { correlationId } = {}) =>
    call('orchestrator', { method: 'POST', query: { action: 'approve_dispatch' }, body: { plant_id, period }, correlationId }),

  // ── PositionService ────────────────────────────────────────────────────
  positions: ({ plant_id, period, region, correlationId } = {}) =>
    call('orchestrator', { query: { action: 'positions', plant_id, period, region }, correlationId }),
  positionsWindow: ({ plant_id, region, from, to, correlationId } = {}) =>
    call('orchestrator', { query: { action: 'positions_window', plant_id, region, from, to }, correlationId }),
  positionsInterval: ({ plant_id, region, from, to, interval_minutes = 30, refinement = 'uniform', correlationId } = {}) =>
    call('orchestrator', { query: { action: 'positions_interval', plant_id, region, from, to, interval_minutes, refinement }, correlationId }),
  audit: ({ plant_id, since, source, correlationId } = {}) =>
    call('orchestrator', { query: { action: 'audit', plant_id, since, source }, correlationId }),
  ledger: ({ plant_id, period, correlation_id, limit, correlationId } = {}) =>
    call('orchestrator', { query: { action: 'ledger', plant_id, period, correlation_id, limit }, correlationId }),
  spAudit: ({ plant_id, period, correlationId } = {}) =>
    call('orchestrator', { query: { action: 'sp_audit', plant_id, period }, correlationId }),
  importPositions: ({ source, note, mode_replace = 'replace', positions, who }, { correlationId } = {}) =>
    call('orchestrator', { method: 'POST', query: { action: 'import_positions' }, body: { source, note, mode_replace, positions, who }, correlationId }),
  adjustPosition: ({ plant_id, period, region, field, new_value, who, note }, { correlationId } = {}) =>
    call('orchestrator', { method: 'POST', query: { action: 'adjust_position' }, body: { plant_id, period, region, field, new_value, who, note }, correlationId }),
  positionAtTs: ({ plant_id, period, ts, correlationId } = {}) =>
    call('orchestrator', { query: { action: 'position_at_ts', plant_id, period, ts }, correlationId }),
};

// ─── Observability — same shape across all six modules ─────────────────────
const MODULES = ['marketdata', 'strategy', 'algo', 'grid', 'dispatch', 'orchestrator'];

export const observability = {
  health: () => Promise.all(MODULES.map((m) => call(m, { query: { action: 'health' } }).catch((e) => ({ source: m, ok: false, error: e.message })))),

  events: ({ correlation_id, since, limit = 100 } = {}) =>
    Promise.all(MODULES.map((m) => call(m, { query: { action: 'events', correlation_id, since, limit } }).catch(() => ({ source: m, events: [], count: 0 })))),

  trace: (correlation_id) =>
    Promise.all(MODULES.map((m) => call(m, { query: { action: 'trace', correlation_id } }).catch(() => ({ source: m, events: [] })))),

  stats: () =>
    Promise.all(MODULES.map((m) => call(m, { query: { action: 'stats' } }).catch(() => ({ source: m, stats: [] })))),

  debug: () =>
    Promise.all(MODULES.map((m) => call(m, { query: { action: 'debug' } }).catch((e) => ({ source: m, error: e.message })))),

  clearAll: () =>
    Promise.all(MODULES.map((m) => call(m, { method: 'POST', query: { action: 'clear' } }).catch((e) => ({ source: m, error: e.message })))),
};

// ─── End-to-end orchestration — drives the demo ─────────────────────────────
/**
 * Run one full Strategy → Algo → Grid → Dispatch cycle under a single
 * correlation_id. Returns intermediate results so the UI can show each step
 * as it completes.
 *
 * @param {Object} opts
 * @param {string} [opts.market='GB']
 * @param {string} [opts.product='DA']
 * @param {Function} [opts.onStep]   Called as ({ step, status, result }).
 */
export async function runEndToEnd({ market = 'GB', product = 'DA', onStep } = {}) {
  const correlationId = newCorrelationId('e2e');
  const step = (s, status, result) => onStep && onStep({ step: s, status, result });

  step('strategy', 'running');
  const strategyRes = await strategy.run({ market, product, correlationId });
  step('strategy', 'done', strategyRes.data);

  step('algo', 'running');
  const algoRes = await algo.submit(strategyRes.data.intents, { correlationId });
  step('algo', 'done', algoRes.data);

  step('grid', 'running');
  const positions = await algo.positions({ correlationId });
  const gridRes = await grid.submitPositions(positions.data, { correlationId });
  step('grid', 'done', gridRes.data);

  step('dispatch', 'running');
  const acceptances = (gridRes.data || []).map((x) => ({
    plant_id: x.acceptance.plant_id,
    period: x.acceptance.period,
    mw: x.submission.payload.mw,
  }));
  const dispatchRes = await dispatch.applyAcceptances(acceptances, { correlationId });
  step('dispatch', 'done', dispatchRes.data);

  return {
    correlationId,
    strategy: strategyRes.data,
    algo: algoRes.data,
    grid: gridRes.data,
    dispatch: dispatchRes.data,
  };
}

export const PLATFORM_MODULES = MODULES;
